// The wizard's work-in-progress, kept somewhere a render cannot lose it.
//
// Two things make this necessary rather than nice. A visitor can now fill in the
// whole wizard before they have an account, so their answers have to survive the
// moment the app flips from signed-out to signed-in and the wizard remounts on a
// different route. And a wizard that lives only in component state loses four
// steps of typing to an accidental reload, which was already true for customers
// who had signed in.
//
// sessionStorage, not localStorage: a draft is meant to outlive a remount, not
// to greet someone a week later with a half-finished form they have forgotten
// writing. It also dies with the tab, which is the right lifetime for something
// typed on a shared computer.

import { DEFAULT_THEME } from "./theme";
import type { AgentTheme, KnowledgeSource } from "./types";

const KEY = "buildbotsnow.agentDraft";

// The wizard's steps, in order. Here rather than in the wizard because a draft's
// `step` indexes into it, and the agent list names the step a draft stopped at.
export const STEPS = ["Basics", "Knowledge", "Behavior", "Where it runs"];

export interface AgentDraft {
  step: number;
  name: string;
  businessName: string;
  description: string;
  tone: string;
  customInstructions: string;
  sources: KnowledgeSource[];
  questionsPerSession: number | null;
  theme: AgentTheme;
  domainsText: string;
  // Set when someone asked to create the agent and we interrupted them to sign
  // up. It is what tells the wizard, once it remounts with an account attached,
  // that the customer has already pressed the button and is not being shown a
  // form to fill in again.
  submitWhenReady: boolean;
}

export function saveDraft(draft: AgentDraft): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(draft));
  } catch {
    // Private mode, a full quota, or storage switched off. A draft that cannot
    // be saved is not worth breaking the wizard over -- they lose the safety
    // net, not the form in front of them.
  }
}

export function loadDraft(): AgentDraft | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// Whether this tab holds a wizard somebody actually started filling in. The
// wizard saves on mount, so an untouched form leaves a draft behind too; that
// one is not worth sending anybody back to.
export function hasDraftInProgress(): boolean {
  const draft = loadDraft();
  if (!draft) return false;
  return (
    draft.submitWhenReady ||
    draft.step > 0 ||
    [draft.name, draft.businessName, draft.description, draft.customInstructions, draft.domainsText].some(
      (value) => value.trim() !== ""
    ) ||
    JSON.stringify(draft.theme) !== JSON.stringify(DEFAULT_THEME)
  );
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do, and nothing worth telling the customer about.
  }
}

// Anything in sessionStorage is attacker-controlled in the sense that matters
// here: it is also *old*-version-controlled. A draft written by a previous
// deploy can be missing fields this build now reads, so the shape is checked
// rather than trusted, and a draft that fails the check is dropped instead of
// being spread into state where it would surface as undefined in an input.
function isDraft(value: unknown): value is AgentDraft {
  if (typeof value !== "object" || value === null) return false;
  const d = value as Record<string, unknown>;

  return (
    typeof d.step === "number" &&
    typeof d.name === "string" &&
    typeof d.businessName === "string" &&
    typeof d.description === "string" &&
    typeof d.tone === "string" &&
    typeof d.customInstructions === "string" &&
    Array.isArray(d.sources) &&
    (d.questionsPerSession === null || typeof d.questionsPerSession === "number") &&
    typeof d.theme === "object" &&
    d.theme !== null &&
    typeof d.domainsText === "string" &&
    typeof d.submitWhenReady === "boolean"
  );
}
